import { useCallback, useEffect, useRef } from 'react'
import PricingCard from './PricingCard'

// The card width the track's centring padding is derived from. Kept here rather than only in the
// class string so the two cannot drift apart — if the card resizes, both change together.
const CARD_WIDTH = 282

// M2.2: below md the three cards used to stack vertically, with a separate
// Starter/Pro/Ultimate tab strip driving the comparison table. Микола tested both and
// chose the horizontal carousel, where the swipe itself is the toggle — so the tab strip
// is gone (M2.2.1) and whichever card you have swiped to is the column the table shows.
//
// The cards are a fixed 282px, so on a 375px screen the next one peeks in by ~45px. That
// peek is the affordance that there is more to the right; there is deliberately no dot
// strip, because a second control is precisely what he asked us to remove.
//
// From md up this is not a carousel at all — it wraps and centres exactly as before, and
// the table shows all three columns, so `selected` stops mattering.
const PlanCarousel = ({ plans, billing, onSelect, onHover }) => {
  const trackRef = useRef(null)
  const frameRef = useRef(0)

  // Whichever card's centre is nearest the track's centre is the one in view. Centre to centre
  // rather than edge against a hard-coded inset, so M1.1's centring padding — or any later change
  // to it — cannot put this out of step with the layout. At the current 306px card pitch both
  // readings happen to pick the same card; this is the coupling removed, not a bug fixed.
  // Deriving it from scroll offset rather than waiting for the snap to settle keeps the table in
  // step during the swipe instead of jumping once the finger lifts.
  const syncFromScroll = useCallback(() => {
    cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(() => {
      const track = trackRef.current
      if (!track) return
      const cards = Array.from(track.querySelectorAll('[data-plan]'))
      if (!cards.length) return

      const trackRect = track.getBoundingClientRect()
      const anchor = trackRect.left + trackRect.width / 2
      let nearest = cards[0]
      let nearestGap = Infinity
      for (const card of cards) {
        const rect = card.getBoundingClientRect()
        const gap = Math.abs(rect.left + rect.width / 2 - anchor)
        if (gap < nearestGap) {
          nearestGap = gap
          nearest = card
        }
      }
      onSelect(nearest.dataset.plan)
    })
  }, [onSelect])

  useEffect(() => () => cancelAnimationFrame(frameRef.current), [])

  return (
    <div className="self-stretch mt-12">
      <div
        ref={trackRef}
        onScroll={syncFromScroll}
        /* M1.1: the cards snap centred rather than to the page gutter, so the first and last need
           half the leftover width as padding to be able to reach the middle — without it a
           `snap-center` track cannot centre its end cards at all. The peek of the neighbour is now
           symmetric, which is the affordance that there is more in both directions. */
        style={{ '--card-w': `${CARD_WIDTH}px` }}
        className="
          flex items-stretch gap-6
          -mx-6 pb-2 overflow-x-auto snap-x snap-mandatory
          px-[max(24px,calc((100vw-var(--card-w))/2))]
          [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          md:mx-0 md:px-0 md:pb-0 md:overflow-visible md:snap-none md:flex-wrap md:justify-center
        "
      >
        {plans.map((plan) => (
          <div
            key={plan.id}
            data-plan={plan.id}
            className="shrink-0 snap-center"
            /* D2.5.2: hovering a card thickens its column under Highlights, so the eye can
               carry the card across to the table — they are not aligned to each other.
               Pointer only; the emphasis itself is gated to md so a tap cannot leave a
               touch screen stuck in a hover state (the M2.6 problem). */
            onMouseEnter={() => onHover(plan.id)}
            onMouseLeave={() => onHover(null)}
          >
            <PricingCard {...plan} billing={billing} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default PlanCarousel
